import { guid, DateRange, UTC, TimeSpan, TimestampRange } from 'rewire-common';
import { observable, root, watch } from 'rewire-core';
import { IColumn, IGrid, IRowData, createColumn, createGrid } from 'rewire-grid';
import { IOtherPayment, IEmployeeSummary } from '../DataTypes';
import { formatTime } from '../utility/DateUtils';
import Queries from '../graphql/Queries';
import timecardModel from './TimecardModel';
import authService from '../services/AuthService';

interface ITimeclocksState {
  expanded: boolean;
  visible: boolean;
}

const timeclocksSchema = `
  id
  effective
  eventType
  location
  comment
  elapsed
  lastEditedBy
  lastEditedDate
`;

export function formatElapsed(dt: number | undefined): string {
  if (typeof dt !== 'number') return '';
  return `${Math.trunc(dt)} hours ${Math.round((dt - Math.trunc(dt)) * 60)} minutes`;
}

export class TimeclocksModel {
  state: ITimeclocksState;
  private _grid: IGrid;
  private _employeeId?: string;
  private _earnedDate?: UTC;

  constructor() {
    this.state = observable({
      expanded: false,
      visible: false,
    });
    this.grid = this.createGrid([]);
    setTimeout(() => {
      root(() => {
        watch(
          () => timecardModel.rollupModel.selectedRolledUpTimecard,
          () => {
            this.state.visible = authService.evalSecurityToken('view_timeclocks');
            this.grid.set([]);
            requestAnimationFrame(this.load);
          },
        );
      });
    });
  }

  get visible(): boolean {
    return this.state.visible;
  }

  get expanded(): boolean {
    return this.state.expanded;
  }

  set expanded(expanded: boolean) {
    this.state.expanded = expanded;
    if (expanded) requestAnimationFrame(this.load);
    else {
      this._earnedDate = undefined;
      this._employeeId = undefined;
    }
  }

  get effective(): DateRange | undefined {
    return timecardModel.effective;
  }

  get selectedEmployee(): IEmployeeSummary | undefined {
    return timecardModel.selectedEmployee;
  }

  get grid(): IGrid {
    return this._grid;
  }

  set grid(grid: IGrid) {
    this._grid = grid;
  }

  get canEditTimecard(): boolean {
    return false;
  }

  private get columns(): any[] {
    const columns = [
      { name: 'id', label: 'Id', visible: false },
      { name: 'effective', label: 'Effective', type: 'text', map: formatTime },
      { name: 'eventType', label: 'Type', type: 'text' },
      { name: 'location', label: 'Location', type: 'text' },
      { name: 'elapsed', label: 'Elapsed', type: 'text', map: formatElapsed },
      { name: 'comment', label: 'Comment', type: 'text' },
    ];

    return columns;
  }

  private createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    this.columns.forEach((col) => {
      const { name, label, ...opts } = col;
      opts.canSort = false;
      const column = createColumn(name, label, opts);
      cols.push(column);
    });
    return cols;
  }

  private createRows(rowData: any[] | undefined, _cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (rowData) {
      rowData.forEach((row) => {
        const r: IRowData = { id: `${guid()}`, data: row, options: row.options };
        rows.push(r);
      });
    }
    return rows;
  }

  reset() {
    this.grid.set([]);
  }

  load = async () => {
    if (!this.state.expanded || !this.state.visible) return;
    const employee = timecardModel.rollupModel.selectedEmployee;
    const earnedDate = timecardModel.rollupModel.selectedRolledUpTimecard?.earnedDate;
    if (!employee || !earnedDate) return;
    if (employee.id === this._employeeId && this._earnedDate === earnedDate) return;
    this._employeeId = employee.id;
    this._earnedDate = earnedDate;
    console.log(`loading ${this._employeeId}:${this._earnedDate}`);

    const range = {
      min: timecardModel.rollupModel.selectedRolledUpTimecard?.start,
      max: timecardModel.rollupModel.selectedRolledUpTimecard?.end,
    };
    if (!range?.min || range.min.utc > earnedDate.add(2, TimeSpan.hours).utc) range.min = earnedDate.add(2, TimeSpan.hours);
    if (!range?.max || range.max.utc < earnedDate.add(22, TimeSpan.hours).utc) range.max = earnedDate.add(22, TimeSpan.hours);

    // increase proximity threshold
    range.min = range.min.add(-2, TimeSpan.hours);
    range.max = range.max.add(2, TimeSpan.hours);
    const timeclocks =
      (
        await Queries.getTimeclocks(
          timeclocksSchema,
          new TimestampRange(range.min, range.max),
          { employeeIds: [this._employeeId] },
          { effective: { direction: 'asc' } },
          30,
        )
      )?.data ?? [];
    this.grid.set(timeclocks.map((r) => ({ id: r.id, data: r })));
  };

  private createGrid(results: IOtherPayment[]): IGrid {
    const cols: IColumn[] = this.createColumns();
    const rows: IRowData[] = this.createRows(results, cols);
    const grid: IGrid = createGrid(rows, cols);
    grid.rowHeight = 32;
    grid.readOnly = true;
    return grid;
  }
}

const timeclocksModel = new TimeclocksModel();
export default timeclocksModel;
